import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import darkLogo from "../../assets/images/darkLogo.svg";
import google from "../../assets/images/google.svg";
import microsoft from "../../assets/images/microsoft.svg";
import email from "../../assets/images/email.svg";
import { FaAngleDown } from "react-icons/fa6";
import Popup from "../Popup/Popup";
import axios from "axios";
import { useForm } from "react-hook-form";
import { BRAINY_BASE_URL } from "../../config/config";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

// ✅ Login validation schema
const loginSchema = yup.object().shape({
  email: yup.string().email("Invalid email format").required("Email is required"),
  password: yup.string().required("Password is required"),
});

// ✅ Signup validation schema
const signupSchema = yup.object().shape({
  email: yup.string().email("Invalid email format").required("Email is required"),
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Minimum 8 characters"),
});

function Header() {
  const navigate = useNavigate();

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showLoginEmail, setShowLoginEmail] = useState(false);
  const [showSetPassword, setShowSetPassword] = useState(false);

  // Login form
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginSchema),
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleEmailLogin = async (data) => {
    setLoading(true);
    setErrorMessage("");

    try {
      const res = await axios.post(`${BRAINY_BASE_URL}user/supabase-login`, {
        email: data.email,
        password: data.password,
      });

      if (res.data.isSuccess) {
        localStorage.setItem("token", res.data.token);
        setShowLoginEmail(false);
        // navigate("/brainy-dashboard");
        window.location.href = "http://brainy-agent-csv-analysis.s3-website-us-east-1.amazonaws.com";
      } else {
        setErrorMessage(res.data.message || "Login failed");
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Signup form
  const {
    register: registerSignup,
    handleSubmit: handleSubmitSignup,
    formState: { errors: signupErrors },
  } = useForm({
    resolver: yupResolver(signupSchema),
  });
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState("");

  const handleSignup = async (data) => {
    setSignupLoading(true);
    setSignupError("");

    try {
      const res = await axios.post(`${BRAINY_BASE_URL}user/supabase-signup`, {
        email: data.email,
        password: data.password,
      });

      if (res?.data?.success) {
        setShowSignup(true);
        setSignupError(res.data.message || "Signup failed");
        // setShowLoginEmail(true); // Redirect to login after signup
      } else {
        setSignupError(res.data.message || "Signup failed");
      }
    } catch (err) {
      setSignupError(err.response?.data?.message || "Something went wrong");
    } finally {
      setSignupLoading(false);
    }
  };

  return (
    <>
      {/* Header Navbar */}
      <div className="header">
        <img src={darkLogo} alt="logo" onClick={() => navigate("/")} />
        <nav className="d-flex gap-5 position-relative">
          <div className="dropdown-wrapper" onClick={toggleDropdown}>
            <a className="text-sm d-flex align-items-center gap-2">
              <span className={`arrow ${isDropdownOpen ? "rotate" : ""}`}>
                <FaAngleDown size={14} />
              </span>
              What is Brainyagent
            </a>

            {isDropdownOpen && (
              <div className="dropdown-menu">
                <a onClick={() => navigate("/chat-with-data")}>
                  Chat With Your Data
                </a>
                <a onClick={() => navigate("/data-visualization")}>
                  Interactive Data Visualization
                </a>
                <a onClick={() => navigate("/connect-db")}>Connect DBs</a>
              </div>
            )}
          </div>

          <a className="text-sm font-medium hover:underline underline-offset-4">
            Resources
          </a>
          <a className="text-sm font-medium hover:underline underline-offset-4">
            Security
          </a>
          <a
            className="text-sm font-medium hover:underline underline-offset-4"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/pricing")}
          >
            Pricing
          </a>
        </nav>

        <div className="d-flex align-items-center gap-2">
          <button
            className="theme-rounded-outline-btn"
            onClick={() => setShowLogin(true)}
          >
            Log In
          </button>
          <button
            className="theme-rounded-btn"
            onClick={() => setShowSignup(true)}
          >
            Sign Up
          </button>
        </div>
      </div>

      {/* Login with Email Popup */}
      <Popup show={showLoginEmail} handleClose={() => setShowLoginEmail(false)}>
        <div className="d-flex flex-column" style={{ gap: "24px" }}>
          <h3 className="form-heading">Sign in with your email</h3>
          <div className="common-form">
            <form onSubmit={handleSubmit(handleEmailLogin)} noValidate>
              {/* Email field */}
              <div className="input-container">
                <input
                  type="email"
                  className="input"
                  placeholder="Enter your email"
                  {...register("email")}
                />
              </div>
              {errors.email && (
                <p className="error-message">{errors.email.message}</p>
              )}

              {/* Password field */}
              <div className="input-container">
                <input
                  type="password"
                  className="input"
                  placeholder="Enter your password"
                  {...register("password")}
                />
              </div>
              {errors.password && (
                <p className="error-message">{errors.password.message}</p>
              )}

              {/* API error */}
              {errorMessage && (
                <p className="error-message">{errorMessage}</p>
              )}

              <button
                className="w-100 theme-rounded-btn"
                type="submit"
                disabled={loading}
              >
                {loading ? "Logging in..." : "Log In"}
              </button>
            </form>
          </div>

          <div className="text-center">
            <a
              style={{
                color: "#000000",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => {
                setShowLoginEmail(false);
                setShowSetPassword(true);
              }}
            >
              Forgot your password?
            </a>
          </div>

          <div className="text-center">
            New here?{" "}
            <a
              onClick={() => {
                setShowLoginEmail(false);
                setShowSignup(true);
              }}
              style={{
                color: "#000000",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Sign up for BrainyAgent
            </a>
          </div>
        </div>
      </Popup>

      {/* Login Popup */}
      <Popup show={showLogin} handleClose={() => setShowLogin(false)}>
        <div className="d-flex flex-column" style={{ gap: "24px" }}>
          <h3 className="form-heading" style={{ fontSize: "36px" }}>
            Welcome back 👋 Let’s get you signed in
          </h3>
          <div className="signupTag">
            <img src={google} alt="" /> Continue with Google
          </div>
          <div className="signupTag">
            <img src={microsoft} alt="" /> Continue with Microsoft
          </div>
          <div
            className="signupTag"
            onClick={() => {
              setShowLogin(false);
              setShowLoginEmail(true);
            }}
          >
            <img src={email} alt="" /> Continue with Email
          </div>
          <div className="text-center">
            New here?{" "}
            <a
              style={{
                color: "#000000",
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() => {
                setShowLogin(false);
                setShowSignup(true);
              }}
            >
              Sign up for BrainyAgent
            </a>
          </div>
        </div>
      </Popup>

      {/* Signup Popup */}
      <Popup show={showSignup} handleClose={() => setShowSignup(false)}>
        <div className="d-flex flex-column" style={{ gap: "24px" }}>
          <h3 className="form-heading" style={{ fontSize: "36px" }}>
            Create Your Account
          </h3>
          <h6 className="m-0">Sign up to get started with BrainyAgent</h6>
          <div className="signupTag">
            <img src={google} alt="" /> Sign up with Google
          </div>
          <div className="signupTag">
            <img src={microsoft} alt="" /> Sign up with Microsoft
          </div>
          <div className="d-flex align-items-center gap-3">
            <hr className="flex-grow-1" />
            <span className="px-2">or use your email</span>
            <hr className="flex-grow-1" />
          </div>

          <div className="common-form">
            <form onSubmit={handleSubmitSignup(handleSignup)} noValidate>
              <div className="input-container">
                <input
                  type="email"
                  className="input"
                  placeholder="Enter your email"
                  {...registerSignup("email")}
                />
              </div>
              {signupErrors.email && (
                <p className="error-message">{signupErrors.email.message}</p>
              )}

              <div className="input-container">
                <input
                  type="password"
                  className="input"
                  placeholder="Enter your password"
                  {...registerSignup("password")}
                />
              </div>
              {signupErrors.password && (
                <p className="error-message">{signupErrors.password.message}</p>
              )}

              {signupError && <p className="error-message">{signupError}</p>}

              <button
                className="w-100 theme-rounded-btn"
                type="submit"
                disabled={signupLoading}
              >
                {signupLoading ? "Signing up..." : "Continue"}
              </button>
            </form>
          </div>

          <div className="text-center">
            Already have an account?{" "}
            <a
              style={{
                color: "#000000",
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() => {
                setShowSignup(false);
                setShowLogin(true);
              }}
            >
              Log in here
            </a>
          </div>
        </div>
      </Popup>

      {/* Forgot password popup */}
      <Popup show={showSetPassword} handleClose={() => setShowSetPassword(false)}>
        <div className="d-flex flex-column" style={{ gap: "24px" }}>
          <h3 className="form-heading" style={{ fontSize: "36px" }}>
            Set your new password
          </h3>
          <h6 className="m-0">
            Almost there. Choose a strong password you haven't used before.
          </h6>
          <ul>
            <li>At least 8 characters long</li>
            <li>One uppercase and one lowercase letter</li>
            <li>One number and one special character</li>
          </ul>
          <div className="common-form">
            <form className="w-75 mx-auto" noValidate>
              <div className="input-container">
                <input type="password" className="input" placeholder="New password" />
              </div>
              <div className="input-container">
                <input
                  type="password"
                  className="input"
                  placeholder="Confirm new password"
                />
              </div>
              <button className="w-100 theme-rounded-btn">Update Password</button>
            </form>
          </div>
          <div className="text-center text-success">
            ✅ Password updated. You’re all set.
          </div>
          <div className="text-center">
            <a
              style={{
                color: "#000000",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => {
                setShowSetPassword(false);
                setShowLogin(true);
              }}
            >
              Back to login
            </a>
          </div>
        </div>
      </Popup>
    </>
  );
}

export default Header;
